/* In-memory AgentClient for mock mode (no gateway configured). Seeded from the
 * same agents catalog the analytics mock derives from, so the rollup on the
 * home grid and the registered agents here line up. Fully mutable; errors
 * mirror the contract `{ error }` envelope. */

import type { AgentClient } from "./AgentClient";
import type {
  Agent,
  AgentCreate,
  AgentOptimization,
  AgentUpdate,
} from "./types";
import type { ApiError } from "../analytics";
import { DASHBOARD_NOW } from "../analytics";
import { AGENTS } from "../analytics/mock/catalog";

function notFound(agentId: string): { error: ApiError } {
  return {
    error: { code: "not_found", message: `agent_id '${agentId}' not found` },
  };
}

function pseudoUuid(seed: string): string {
  let h = 0x811c9dc5;
  const hex = (n: number) => {
    const out: string[] = [];
    for (let i = 0; i < n; i += 1) {
      h = Math.imul(h ^ seed.charCodeAt(i % seed.length), 0x01000193) >>> 0;
      out.push(((h >>> (i % 24)) & 0xf).toString(16));
    }
    return out.join("");
  };
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${hex(4)}-${hex(12)}`;
}

const NOW = DASHBOARD_NOW.toISOString();

/* A representative optimization catalog for mock mode, mirroring what the BE
 * serves from GET /settings/agents/{id}/optimizations (grouped by family, with
 * `keys` + `safety` + availability). Includes a merged card (multiple keys) so
 * mock mode exercises the toggle-all-keys path. Independent of the real catalog
 * on purpose — kept here so agents/ takes no dependency on configs/. */
const MOCK_OPTIMIZATIONS: readonly AgentOptimization[] = [
  {
    id: "tool_result_compression",
    keys: ["rtk", "local_compression"],
    name: "Tool-result compression",
    description:
      "Shrinks bulky tool results, reversibly — RTK plus in-process pruning.",
    category: "tool_results",
    availability: "available",
    safety: "lossless",
  },
  {
    id: "tool_result_sanitization",
    keys: ["tool_result_sanitization"],
    name: "Tool-result sanitization",
    description: "Strips noise and secrets from tool results.",
    category: "tool_results",
    availability: "available",
    safety: "lossless",
  },
  {
    id: "tool_result_dedup",
    keys: ["tool_result_dedup"],
    name: "Tool-result dedup",
    description: "Drops duplicate copies of a repeated tool result.",
    category: "tool_results",
    availability: "available",
    safety: "lossless",
  },
  {
    id: "tool_result_truncation",
    keys: ["tool_result_truncation"],
    name: "Tool-result truncation",
    description: "Caps oversized tool results at a safe length.",
    category: "tool_results",
    availability: "available",
    safety: "lossy",
  },
  {
    id: "ccr",
    keys: ["ccr"],
    name: "Result offload (CCR)",
    description: "Parks heavy results in storage, hands the model a stub.",
    category: "tool_results",
    availability: "coming_soon",
    safety: "lossy",
  },
  {
    id: "tool_definition_compression",
    keys: ["tool_definition_compression"],
    name: "Tool-definition compression",
    description: "Re-encodes the tool schema into fewer tokens, losslessly.",
    category: "tool_defs",
    availability: "available",
    safety: "lossless",
  },
  {
    id: "routing",
    keys: ["routing"],
    name: "Model routing",
    description:
      "Routes each request to the cheapest model that holds quality.",
    category: "routing_cache",
    availability: "coming_soon",
    safety: "routing",
  },
  {
    id: "cache_anchor",
    keys: ["cache_anchor"],
    name: "Cache anchoring",
    description:
      "Pins cache breakpoints so the prompt cache keeps hitting. Anthropic models only.",
    category: "routing_cache",
    availability: "available",
    safety: "lossless",
  },
  {
    id: "skill_injection",
    keys: ["skill_injection"],
    name: "Output-token reduction",
    description:
      "Appends a terse-output directive to the system prompt to cut output tokens.",
    category: "prompt",
    availability: "available",
    safety: "lossy",
  },
  {
    id: "pxpipe_prompt",
    keys: ["pxpipe_prompt"],
    name: "Prompt imaging",
    description:
      "Packs system prompt and tools definitions into a compact image representation to reduce input token overhead.",
    category: "prompt",
    availability: "coming_soon",
    safety: "experimental",
  },
  {
    id: "pxpipe_tool_results",
    keys: ["pxpipe_tool_results"],
    name: "Tool result imaging",
    description:
      "Renders oversized tool results as compact images. Requires prompt imaging.",
    category: "tool_results",
    availability: "coming_soon",
    safety: "experimental",
  },
];

// Mock-mode "me" — DEV_USER in identity/client.tsx. Not imported (agents/
// stays independent of identity/client's React context); kept as a literal so
// ownership seeding below reads the same as what a real dev session would see.
const MOCK_SELF_ID = "self";

/** Per-agent ownership seed, exercising every `can_edit` case a viewer meets:
 *  owned-by-me (edit), owned-by-someone-else (view-only, disabled w/ reason),
 *  and unowned/legacy (open to anyone, per OPSS-278). */
const OWNERSHIP: Record<
  string,
  { created_by_user_id: string | null; can_edit: boolean }
> = {
  "cc-platform": { created_by_user_id: MOCK_SELF_ID, can_edit: true },
  "cc-studio": { created_by_user_id: "someone-else", can_edit: false },
  "codex-studio-web": { created_by_user_id: MOCK_SELF_ID, can_edit: true },
  "langchain-review-bot": {
    created_by_user_id: "someone-else",
    can_edit: false,
  },
  // Legacy/unowned: open to anyone, not locked to admins.
  "cc-research": { created_by_user_id: null, can_edit: true },
};

function seedAgents(): Agent[] {
  return AGENTS.map((a) => ({
    id: pseudoUuid(a.agent_id),
    agent_id: a.agent_id,
    name: a.name,
    kind: a.kind,
    description: a.description,
    config_id: a.config_id,
    enabled: true,
    // Exercise both onboarding states: the never-configured agent
    // (config_id === null, i.e. cc-research) reads as not-yet-connected;
    // the rest are established and read used: true.
    used: a.config_id !== null,
    created_by_user_id:
      OWNERSHIP[a.agent_id]?.created_by_user_id ?? MOCK_SELF_ID,
    can_edit: OWNERSHIP[a.agent_id]?.can_edit ?? true,
    created_at: NOW,
    updated_at: NOW,
  }));
}

export interface MockAgentClientOptions {
  delayMs?: number;
}

export class MockAgentClient implements AgentClient {
  private readonly delayMs: number;
  private agents: Map<string, Agent>;

  constructor(opts: MockAgentClientOptions = {}) {
    this.delayMs = opts.delayMs ?? 180;
    this.agents = new Map(seedAgents().map((a) => [a.agent_id, a]));
  }

  private async delay(): Promise<void> {
    if (this.delayMs > 0) await new Promise((r) => setTimeout(r, this.delayMs));
  }

  async list(): Promise<Agent[]> {
    await this.delay();
    return [...this.agents.values()].sort((a, b) =>
      a.agent_id.localeCompare(b.agent_id),
    );
  }

  async get(agentId: string): Promise<Agent> {
    await this.delay();
    const row = this.agents.get(agentId);
    if (!row) throw notFound(agentId);

    return row;
  }

  async create(payload: AgentCreate): Promise<Agent> {
    await this.delay();
    if (this.agents.has(payload.agent_id)) {
      throw {
        error: {
          code: "bad_request",
          message: `agent_id '${payload.agent_id}' already exists`,
        } satisfies ApiError,
      };
    }

    const row: Agent = {
      id: pseudoUuid(payload.agent_id),
      agent_id: payload.agent_id,
      name: payload.name ?? "",
      kind: payload.kind,
      description: payload.description ?? null,
      config_id: payload.config_id ?? null,
      enabled: payload.enabled ?? true,
      // A freshly created agent has never been connected to yet.
      used: false,
      // You always own — and so can edit — what you just created.
      created_by_user_id: MOCK_SELF_ID,
      can_edit: true,
      created_at: NOW,
      updated_at: NOW,
    };
    this.agents.set(row.agent_id, row);
    return row;
  }

  async update(agentId: string, payload: AgentUpdate): Promise<Agent> {
    await this.delay();
    const row = this.agents.get(agentId);
    if (!row) throw notFound(agentId);

    // `used` is server-managed and not part of AgentUpdate, so the `...row`
    // spread preserves it — an update never changes the onboarding latch.
    const next: Agent = {
      ...row,
      name: payload.name ?? row.name,
      kind: payload.kind ?? row.kind,
      description:
        payload.description === undefined
          ? row.description
          : payload.description,
      config_id:
        payload.config_id === undefined ? row.config_id : payload.config_id,
      enabled: payload.enabled ?? row.enabled,
      updated_at: NOW,
    };
    this.agents.set(agentId, next);
    return next;
  }

  async remove(agentId: string): Promise<void> {
    await this.delay();
    if (!this.agents.has(agentId)) throw notFound(agentId);

    this.agents.delete(agentId);
  }

  async listOptimizations(agentId: string): Promise<AgentOptimization[]> {
    await this.delay();
    // Matches the BE: 404 for an unknown agent, else the (kind-independent) list.
    if (!this.agents.has(agentId)) throw notFound(agentId);

    return MOCK_OPTIMIZATIONS.map((o) => ({ ...o }));
  }
}
